(function () {
  'use strict';

  const selectors = {
    sourceButton: 'product-form [data-product-form] [data-add-to-cart]',
    sourceButtonText: '[data-add-to-cart-text]',
    sourcePrice: '.product__price-wrapper [data-product-price]',
    sourceComparePrice: '.product__price-wrapper [data-compare-price]',
    stickyButton: '[data-sticky-add-to-cart-button]',
    stickyButtonText: '[data-sticky-add-to-cart-text]',
    stickyButtonPrices: '[data-sticky-add-to-cart-button-prices]',
    stickyButtonPrice: '[data-sticky-add-to-cart-button-price]',
    stickyButtonComparePrice: '[data-sticky-add-to-cart-button-compare-price]',
    stickyPrice: '[data-sticky-add-to-cart-price]',
    stickyComparePrice: '[data-sticky-add-to-cart-compare-price]',
    stickyVariant: '[data-sticky-add-to-cart-variant]',
  };

  const mirroredStateClasses = ['is-loading', 'btn--success'];
  const visibleBodyClass = 'sticky-add-to-cart-visible';

  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedToProduct) return;
      this.isConnectedToProduct = true;

      this.sourceSection = document.getElementById(this.dataset.sourceSectionId);
      this.productComponent = this.sourceSection?.querySelector('product-component');
      this.stickyButton = this.querySelector(selectors.stickyButton);
      this.stickyButtonText = this.querySelector(selectors.stickyButtonText);
      this.stickyButtonPrices = this.querySelector(selectors.stickyButtonPrices);
      this.stickyButtonPrice = this.querySelector(selectors.stickyButtonPrice);
      this.stickyButtonComparePrice = this.querySelector(selectors.stickyButtonComparePrice);
      this.stickyPrice = this.querySelector(selectors.stickyPrice);
      this.stickyComparePrice = this.querySelector(selectors.stickyComparePrice);
      this.stickyVariant = this.querySelector(selectors.stickyVariant);
      this.abortController = new AbortController();
      this.sourceHasBeenPassed = false;
      this.syncFrame = null;
      this.shouldResolveSource = false;

      if (
        !this.sourceSection
        || !this.productComponent
        || !this.stickyButton
        || !('IntersectionObserver' in window)
      ) {
        this.hide();
        return;
      }

      this.stickyButton.addEventListener('click', () => this.handleClick(), {
        signal: this.abortController.signal,
      });
      document.addEventListener('theme:variant:change', (event) => this.handleVariantChange(event), {
        signal: this.abortController.signal,
      });

      this.resolveSourceElements();
    }

    disconnectedCallback() {
      this.isConnectedToProduct = false;
      this.abortController?.abort();
      this.intersectionObserver?.disconnect();
      this.buttonObserver?.disconnect();
      this.formObserver?.disconnect();
      this.priceObserver?.disconnect();
      document.body.classList.remove(visibleBodyClass);
      if (this.syncFrame !== null) cancelAnimationFrame(this.syncFrame);
    }

    resolveSourceElements() {
      const nextSourceButton = this.findSourceButton();

      if (!nextSourceButton) {
        this.sourceButton = null;
        this.hide();
        return;
      }

      this.intersectionObserver?.disconnect();
      this.buttonObserver?.disconnect();
      this.formObserver?.disconnect();
      this.priceObserver?.disconnect();

      this.sourceButton = nextSourceButton;
      this.sourceButtonText = this.sourceButton.querySelector(selectors.sourceButtonText);
      this.sourcePrice = this.sourceSection.querySelector(selectors.sourcePrice);
      this.sourceComparePrice = this.sourceSection.querySelector(selectors.sourceComparePrice);

      this.intersectionObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;

        this.sourceHasBeenPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.updateVisibility();
      });
      this.intersectionObserver.observe(this.sourceButton);

      this.buttonObserver = new MutationObserver(() => this.queueSync());
      this.buttonObserver.observe(this.sourceButton, {
        attributes: true,
        attributeFilter: ['aria-disabled', 'class', 'disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      const sourceProductForm = this.sourceButton.closest('product-form');
      if (sourceProductForm) {
        this.formObserver = new MutationObserver(() => {
          const activeSourceButton = this.findSourceButton();

          if (activeSourceButton !== this.sourceButton) {
            this.queueSync(true);
          } else {
            this.queueSync();
          }
        });
        this.formObserver.observe(sourceProductForm, {
          attributes: true,
          attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
          childList: true,
          subtree: true,
        });
      }

      const priceWrapper = this.sourcePrice?.closest('.product__price-wrapper');
      if (priceWrapper) {
        this.priceObserver = new MutationObserver(() => this.queueSync());
        this.priceObserver.observe(priceWrapper, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          characterData: true,
          subtree: true,
        });
      }

      const sourceRect = this.sourceButton.getBoundingClientRect();
      this.sourceHasBeenPassed = sourceRect.bottom <= 0;
      this.syncFromSource();
    }

    findSourceButton() {
      const sourceButtons = [...this.sourceSection.querySelectorAll(selectors.sourceButton)];

      return sourceButtons.find((button) => this.isRenderedSourceButton(button))
        || sourceButtons[0]
        || null;
    }

    isRenderedSourceButton(button) {
      if (!(button instanceof HTMLElement) || !button.isConnected || button.hidden) return false;
      if (button.getAttribute('aria-hidden') === 'true') return false;

      const buttonStyles = window.getComputedStyle(button);
      if (buttonStyles.display === 'none' || buttonStyles.visibility === 'hidden') return false;

      const buttonRect = button.getBoundingClientRect();
      return buttonRect.width > 0 && buttonRect.height > 0;
    }

    handleVariantChange(event) {
      const eventTarget = event.target;
      const belongsToProduct = eventTarget === this.productComponent
        || (eventTarget instanceof Element && this.productComponent.contains(eventTarget))
        || (eventTarget instanceof Element && eventTarget.contains(this.productComponent));

      if (!belongsToProduct) return;

      const variant = event.detail?.variant;
      this.updateVariant(variant);

      this.queueSync(true);
    }

    updateVariant(variant) {
      if (!this.stickyVariant) return;

      const variantTitle = variant?.public_title || variant?.title || '';
      const shouldShow = Boolean(variantTitle && variantTitle !== 'Default Title');

      this.stickyVariant.textContent = shouldShow ? variantTitle : '';
      this.stickyVariant.classList.toggle('hidden', !shouldShow);
    }

    queueSync(resolveSource = false) {
      this.shouldResolveSource = this.shouldResolveSource || resolveSource;
      if (this.syncFrame !== null) return;

      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        const shouldResolveSource = this.shouldResolveSource;
        this.shouldResolveSource = false;

        if (shouldResolveSource) {
          this.resolveSourceElements();
        } else {
          this.syncFromSource();
        }
      });
    }

    syncFromSource() {
      if (!this.sourceButton || !this.sourceButton.isConnected) {
        this.resolveSourceElements();
        return;
      }

      const sourceLabel = this.actionLabel(this.sourceButtonText);
      if (sourceLabel && this.stickyButtonText) {
        this.stickyButtonText.textContent = sourceLabel;
      }

      const sourceDisabled = this.sourceButton.disabled
        || this.sourceButton.getAttribute('aria-disabled') === 'true';
      this.stickyButton.disabled = sourceDisabled;
      this.stickyButton.setAttribute('aria-disabled', String(sourceDisabled));

      mirroredStateClasses.forEach((className) => {
        this.stickyButton.classList.toggle(className, this.sourceButton.classList.contains(className));
      });

      const sourcePriceText = this.normalizedText(this.sourcePrice);
      [this.stickyPrice, this.stickyButtonPrice].forEach((priceElement) => {
        if (priceElement && sourcePriceText) priceElement.textContent = sourcePriceText;
      });
      this.stickyButtonPrices?.classList.toggle('hidden', !sourcePriceText);

      const comparePriceText = this.normalizedText(this.sourceComparePrice);
      const comparePriceVisible = Boolean(
        comparePriceText
        && this.sourceComparePrice
        && !this.sourceComparePrice.classList.contains('hidden')
      );

      [this.stickyComparePrice, this.stickyButtonComparePrice].forEach((comparePriceElement) => {
        if (!comparePriceElement) return;
        comparePriceElement.textContent = comparePriceVisible ? comparePriceText : '';
        comparePriceElement.classList.toggle('hidden', !comparePriceVisible);
      });

      const accessibleLabel = [sourceLabel, sourcePriceText].filter(Boolean).join(' ');
      if (accessibleLabel) this.stickyButton.setAttribute('aria-label', accessibleLabel);

      this.updateVisibility();
    }

    actionLabel(element) {
      if (!element) return '';

      const labelClone = element.cloneNode(true);
      labelClone.querySelectorAll('[data-product-price], [data-compare-price]').forEach((priceElement) => {
        priceElement.remove();
      });

      return this.normalizedText(labelClone);
    }

    normalizedText(element) {
      return element?.textContent?.replace(/\s+/g, ' ').trim() || '';
    }

    handleClick() {
      if (!this.sourceButton?.isConnected) {
        this.resolveSourceElements();
      }

      if (!this.sourceButton || this.stickyButton.disabled) return;
      this.sourceButton.click();
    }

    updateVisibility() {
      const sourceIsRendered = this.isRenderedSourceButton(this.sourceButton);
      const shouldShow = sourceIsRendered && this.sourceHasBeenPassed;

      if (shouldShow) {
        this.show();
      } else {
        this.hide();
      }
    }

    show() {
      this.setAttribute('data-visible', '');
      this.setAttribute('aria-hidden', 'false');
      this.inert = false;
      document.body.classList.add(visibleBodyClass);
      if (this.stickyButton) this.stickyButton.removeAttribute('tabindex');
    }

    hide() {
      this.removeAttribute('data-visible');
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;
      document.body.classList.remove(visibleBodyClass);
      if (this.stickyButton) this.stickyButton.setAttribute('tabindex', '-1');
    }
  }

  if (!customElements.get('sticky-add-to-cart')) {
    customElements.define('sticky-add-to-cart', StickyAddToCart);
  }
})();
